import { CaseStudyUIGeneratorConfig } from '../CaseStudyUIGenerator';
import { IconName } from '../components/ui/Icon/Icon';
import { MediaConfigWithSide } from '../types';

type FortifyCaseStudyUITemplateParams = {
  titleBannerProperties: {
    caseStudyTitle: string;
    description: string;
    bannerImageUrl: string;
    companyLogoUrl: string;
    projectName: string;
  };
  introductionProperties: {
    title: string;
    paragraphs: string[];
    tagGroups: { heading: string; items: string[] }[];
    mediaUrl: string;
  };
  businessChallengeProperties: {
    text: string;
    highlightedPhrases: string[];
  };
  metricCardsSectionProperties: {
    sectionTitle: string;
    metrics: {
      iconName: IconName;
      label: string;
      value: string;
    }[];
  };
  solutionsSectionProperties: {
    sectionTitle: string;
    introductionText: string;
    solutions: {
      heading: string;
      problemText: string;
      solutionText: string;
    }[];
  };
  productCardsSectionProperties: {
    sectionTitle: string;
    introductionText: string;
    products: {
      title: string;
      subtitle: string;
      description: string;
      mediaSrc: string;
    }[];
  };
  accordionsSectionProperties: {
    sectionTitle: string;
    firstAccordion: {
      headingTitle: string;
      headingSubtitle: string;
      headingDescription: string;
      contentIntroductionText: string;
      contentSubtitle: string;
      contentBulletPoints: string[];
      contentResultText: string;
      contentMedia: MediaConfigWithSide;
    };
    secondAccordion: {
      headingTitle: string;
      headingSubtitle: string;
      headingDescription: string;
      contentIntroductionText: string;
      contentSubtitle: string;
      contentBulletPoints: string[];
      contentResultText: string;
      contentMedia: MediaConfigWithSide;
      contentDiagramSteps: (
        | {
            type: 'entity';
            iconName: IconName;
            title: string;
            subtitle: string;
          }
        | {
            type: 'connection';
            title: string;
          }
      )[];
    };
    thirdAccordion: {
      headingTitle: string;
      headingSubtitle: string;
      headingDescription: string;
      contentIntroductionText: string;
      contentSubtitle: string;
      contentBulletPoints: string[];
      contentResultText: string;
      contentMedia: MediaConfigWithSide;
      textWithIconHeading: string;
      textWithIconCards: {
        heading: string;
        text: string;
        iconName: IconName;
      }[];
    };
  };
  outcomeSectionProperties: {
    sectionTitle: string;
    paragraphs: string[];
    media: MediaConfigWithSide;
  };
};

export const createFortifyCaseStudyUITemplate = ({
  titleBannerProperties,
  introductionProperties,
  businessChallengeProperties,
  metricCardsSectionProperties,
  solutionsSectionProperties,
  productCardsSectionProperties,
  accordionsSectionProperties,
  outcomeSectionProperties,
}: FortifyCaseStudyUITemplateParams): CaseStudyUIGeneratorConfig[] => {
  return [
    {
      type: 'titleBanner',
      properties: {
        caseStudyTitle: titleBannerProperties.caseStudyTitle,
        description: titleBannerProperties.description,
        bannerImageUrl: titleBannerProperties.bannerImageUrl,
        companyLogoUrl: titleBannerProperties.companyLogoUrl,
        projectName: titleBannerProperties.projectName,
      },
    },
    {
      type: 'introduction',
      properties: {
        title: introductionProperties.title,
        paragraphs: introductionProperties.paragraphs,
        tagGroups: introductionProperties.tagGroups,
        mediaUrl: introductionProperties.mediaUrl,
      },
    },
    {
      type: 'businessChallenge',
      properties: {
        text: businessChallengeProperties.text,
        highlightedPhrases: businessChallengeProperties.highlightedPhrases,
      },
    },
    {
      type: 'titledSection',
      title: metricCardsSectionProperties.sectionTitle,
      contents: [
        {
          type: 'metricCards',
          properties: {
            metrics: metricCardsSectionProperties.metrics,
          },
        },
      ],
    },
    {
      type: 'titledSection',
      title: solutionsSectionProperties.sectionTitle,
      contents: [
        {
          type: 'introductionText',
          properties: {
            text: solutionsSectionProperties.introductionText,
          },
        },
        {
          type: 'solutions',
          properties: {
            solutions: solutionsSectionProperties.solutions,
          },
        },
      ],
    },
    {
      type: 'titledSection',
      title: productCardsSectionProperties.sectionTitle,
      contents: [
        {
          type: 'introductionText',
          properties: {
            text: productCardsSectionProperties.introductionText,
          },
        },
        {
          type: 'productCards',
          properties: {
            products: productCardsSectionProperties.products,
          },
        },
      ],
    },
    {
      type: 'titledSection',
      title: accordionsSectionProperties.sectionTitle,
      contents: [
        {
          type: 'accordion',
          properties: {
            items: [
              {
                title: accordionsSectionProperties.firstAccordion.headingTitle,
                substitle: accordionsSectionProperties.firstAccordion.headingSubtitle,
                description: accordionsSectionProperties.firstAccordion.headingDescription,
                accordionContent: [
                  {
                    type: 'introductionText',
                    properties: {
                      text: accordionsSectionProperties.firstAccordion.contentIntroductionText,
                    },
                  },
                  {
                    type: 'dashedTitleSection',
                    properties: {
                      title: accordionsSectionProperties.firstAccordion.contentSubtitle,
                      contents: [
                        {
                          type: 'bulletPoints',
                          properties: {
                            bulletPoints: accordionsSectionProperties.firstAccordion.contentBulletPoints,
                            media: accordionsSectionProperties.firstAccordion.contentMedia,
                            resultText: accordionsSectionProperties.firstAccordion.contentResultText,
                          },
                        },
                      ],
                    },
                  },
                ],
              },
              {
                title: accordionsSectionProperties.secondAccordion.headingTitle,
                substitle: accordionsSectionProperties.secondAccordion.headingSubtitle,
                description: accordionsSectionProperties.secondAccordion.headingDescription,
                accordionContent: [
                  {
                    type: 'introductionText',
                    properties: {
                      text: accordionsSectionProperties.secondAccordion.contentIntroductionText,
                    },
                  },
                  {
                    type: 'dashedTitleSection',
                    properties: {
                      title: accordionsSectionProperties.secondAccordion.contentSubtitle,
                      contents: [
                        {
                          type: 'workflowDiagram',
                          properties: {
                            steps: accordionsSectionProperties.secondAccordion.contentDiagramSteps,
                          },
                        },
                        {
                          type: 'bulletPoints',
                          properties: {
                            bulletPoints: accordionsSectionProperties.secondAccordion.contentBulletPoints,
                            media: accordionsSectionProperties.secondAccordion.contentMedia,
                            resultText: accordionsSectionProperties.secondAccordion.contentResultText,
                          },
                        },
                      ],
                    },
                  },
                ],
              },
              //THIRD
              {
                title: accordionsSectionProperties.thirdAccordion.headingTitle,
                substitle: accordionsSectionProperties.thirdAccordion.headingSubtitle,
                description: accordionsSectionProperties.thirdAccordion.headingDescription,
                accordionContent: [
                  {
                    type: 'introductionText',
                    properties: {
                      text: accordionsSectionProperties.thirdAccordion.contentIntroductionText,
                    },
                  },
                  {
                    type: 'dashedTitleSection',
                    properties: {
                      title: accordionsSectionProperties.thirdAccordion.contentSubtitle,
                      contents: [
                        {
                          type: 'textWithIconCards',
                          properties: {
                            heading: accordionsSectionProperties.thirdAccordion.textWithIconHeading,
                            cards: accordionsSectionProperties.thirdAccordion.textWithIconCards,
                          },
                        },
                        {
                          type: 'bulletPoints',
                          properties: {
                            bulletPoints: accordionsSectionProperties.thirdAccordion.contentBulletPoints,
                            media: accordionsSectionProperties.thirdAccordion.contentMedia,
                            resultText: accordionsSectionProperties.thirdAccordion.contentResultText,
                          },
                        },
                      ],
                    },
                  },
                ],
              },
            ],
          },
        },
      ],
    },
    {
      type: 'titledSection',
      title: outcomeSectionProperties.sectionTitle,
      contents: [
        {
          type: 'textWithMedia',
          properties: {
            paragraphs: outcomeSectionProperties.paragraphs,
            media: outcomeSectionProperties.media,
          },
        },
      ],
    },
  ];
};
